import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Logo } from "./Bits";
import { useAuth } from "@/context/AuthContext";
import { Menu, X, MapPin, ChevronDown, LayoutDashboard } from "lucide-react";
import { FIND_CARE_MENU, TAG_STYLES } from "@/data/findCareMenu";

const portalPath = (role) => (role === "admin" ? "/admin" : role === "carer" ? "/carer" : "/seeker");

export default function Header() {
  const [mega, setMega] = useState(false);
  const [panel, setPanel] = useState("elder");
  const [mobile, setMobile] = useState(false);
  const [city, setCity] = useState("Hyderabad");
  const { user, logout } = useAuth();
  const nav = useNavigate();

  useEffect(() => {
    const close = () => setMega(false);
    window.addEventListener("scroll", close);
    return () => window.removeEventListener("scroll", close);
  }, []);

  useEffect(() => {
    document.body.style.overflow = mobile ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [mobile]);

  const navLinks = [
    { to: "/how-it-works", label: "How it works" },
    { to: "/nri", label: "For NRIs" },
    { to: "/trust-safety", label: "Trust & Safety" },
    { to: "/become-a-carer", label: "Become a Carer" },
  ];

  return (
    <>
      <div className="w-full bg-ink text-ivory text-[13.5px] text-center py-2 px-4" data-testid="announcement-bar">
        Now live in <b className="text-butter">Hyderabad</b> & <b className="text-butter">Bengaluru</b> · Chennai, Pune & Delhi NCR coming soon · <b className="text-butter">Verified carers, booked in minutes</b>
      </div>

      <header className="sticky top-0 z-50 h-[70px] backdrop-blur-[12px] bg-[var(--ivory)]/80 border-b border-line">
        <div className="bmc-wrap h-full flex items-center justify-between gap-4">
          <div className="flex items-center gap-7">
            <Logo />
            <nav className="hidden lg:flex items-center gap-6 text-[15px] font-medium text-ink">
              <button onClick={() => setMega((v) => !v)} className="inline-flex items-center gap-1 hover:text-coral transition-colors" data-testid="nav-find-care">
                Find Care <ChevronDown size={15} className={mega ? "rotate-180 transition-transform" : "transition-transform"} />
              </button>
              {navLinks.map((l) => (
                <Link key={l.to} to={l.to} className="hover:text-coral transition-colors" data-testid={`nav-${l.to.slice(1)}`}>{l.label}</Link>
              ))}
            </nav>
          </div>

          <div className="hidden lg:flex items-center gap-3">
            <button onClick={() => setCity((c) => (c === "Hyderabad" ? "Bengaluru" : "Hyderabad"))}
              className="btn-pill border border-line px-3 py-1.5 text-sm text-ink-soft hover:border-coral" data-testid="city-toggle">
              <MapPin size={14} /> {city}
            </button>
            {user ? (
              <>
                <Link to={portalPath(user.role)} className="btn-pill bg-ink text-ivory px-4 py-2 text-sm" data-testid="header-dashboard">
                  <LayoutDashboard size={15} /> Dashboard
                </Link>
                <button onClick={() => { logout(); nav("/"); }} className="text-sm text-ink-soft hover:text-coral" data-testid="header-logout">Log out</button>
              </>
            ) : (
              <>
                <Link to="/login" className="text-sm text-ink hover:text-coral" data-testid="nav-login">Log In</Link>
                <Link to="/register" className="btn-pill bg-coral text-white px-5 py-2.5 text-sm hover:bg-coral-deep" data-testid="nav-get-started">Get Started</Link>
              </>
            )}
          </div>

          <button type="button" className="lg:hidden text-ink p-2 -mr-2" onClick={() => setMobile(true)} aria-label="Open menu" data-testid="mobile-menu-open"><Menu /></button>
        </div>

        {mega && (() => {
          const active = FIND_CARE_MENU.find((c) => c.key === panel) || FIND_CARE_MENU[0];
          const go = () => { nav(`/find-care?cat=${active.key}`); setMega(false); };
          return (
            <div className="absolute left-0 right-0 top-[70px] bg-card border-b border-line shadow-bmc-lg animate-fade-up max-h-[calc(100vh-70px)] overflow-y-auto" data-testid="mega-menu">
              <div className="max-w-[1280px] mx-auto flex flex-col lg:flex-row">
                {/* Left category rail */}
                <div className="lg:w-[230px] shrink-0 lg:border-r border-line py-3 lg:py-6 flex lg:flex-col flex-wrap gap-1 px-3 lg:px-0 border-b lg:border-b-0">
                  {FIND_CARE_MENU.map((c) => {
                    const on = c.key === panel;
                    return (
                      <button key={c.key} onMouseEnter={() => setPanel(c.key)} onClick={() => setPanel(c.key)} data-testid={`mega-cat-${c.key}`}
                        className={`relative flex items-center gap-3 text-left text-sm font-medium transition-colors rounded-full lg:rounded-none px-3 lg:px-5 py-2 lg:py-[11px] ${on ? "bg-ivory-2 text-coral" : "text-[#4a4038] hover:bg-ivory-2 hover:text-coral"}`}>
                        <span className="text-base">{c.icon}</span>
                        <span>{c.label}</span>
                        {on && <span className="hidden lg:block absolute right-0 top-1/2 -translate-y-1/2 w-[3px] h-3/5 bg-coral rounded-l" />}
                      </button>
                    );
                  })}
                </div>

                {/* Right service panel */}
                <div className="flex-1 px-6 lg:px-8 py-6 lg:py-7" data-testid={`mega-panel-${active.key}`}>
                  <h3 className="font-display font-bold text-[22px] text-ink">{active.title}</h3>
                  <p className="text-[13px] text-ink-soft mt-1 max-w-[580px] leading-relaxed">{active.desc}</p>

                  <div className="flex flex-wrap gap-2 mt-4 mb-5">
                    {active.modes.map((m) => (
                      <span key={m} className="text-xs font-medium border border-line bg-card text-ink-soft px-3 py-1.5 rounded-full">{m}</span>
                    ))}
                  </div>

                  <div className="grid gap-3" style={{ gridTemplateColumns: "repeat(auto-fill,minmax(160px,1fr))" }}>
                    {active.services.map((s) => (
                      <button key={s.name} onClick={go} data-testid={`mega-service-${active.key}-${s.name.replace(/[^a-z]+/gi, "-").toLowerCase()}`}
                        className="group text-left bg-ivory border-[1.5px] border-line rounded-r p-4 flex flex-col gap-2 transition-all hover:border-coral hover:bg-coral-soft hover:-translate-y-0.5 hover:shadow-bmc">
                        <span className="text-2xl leading-none">{s.icon}</span>
                        <span className="text-[13px] font-semibold text-ink leading-snug">{s.name}</span>
                        <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full w-fit" style={TAG_STYLES[s.t]}>{s.tag}</span>
                      </button>
                    ))}
                  </div>

                  <div className="flex flex-col sm:flex-row sm:items-center gap-4 pt-4 mt-5 border-t border-line">
                    <p className="text-xs text-ink-faint flex-1">{active.note}</p>
                    <button onClick={go} className="btn-pill bg-coral text-white px-5 py-2 text-[13px] hover:bg-coral-deep whitespace-nowrap self-start" data-testid={`mega-cta-${active.key}`}>
                      {active.cta} →
                    </button>
                  </div>
                </div>
              </div>
            </div>
          );
        })()}
      </header>

      {mobile && (
        <div className="fixed inset-0 z-[60] bg-ink text-ivory overflow-y-auto lg:hidden animate-fade-up" data-testid="mobile-menu">
          <div className="p-6">
            <div className="flex justify-between items-center mb-8">
              <Logo light />
              <button type="button" onClick={() => setMobile(false)} className="p-2 -mr-2" aria-label="Close menu" data-testid="mobile-menu-close"><X /></button>
            </div>

            <p className="text-xs font-bold tracking-widest text-butter uppercase mb-3">Find Care</p>
            <div className="grid grid-cols-2 gap-2 mb-6">
              {FIND_CARE_MENU.map((c) => (
                <Link key={c.key} to={`/find-care?cat=${c.key}`} onClick={() => setMobile(false)}
                  className="flex items-center gap-2 bg-white/5 rounded-r px-3 py-3 text-sm" data-testid={`m-cat-${c.key}`}>
                  <span className="text-lg">{c.icon}</span><span className="leading-tight">{c.label}</span>
                </Link>
              ))}
            </div>

            <div className="flex flex-col gap-1 text-lg border-t border-white/15 pt-5">
              {navLinks.map((l) => <Link key={l.to} to={l.to} onClick={() => setMobile(false)} className="py-2">{l.label}</Link>)}
            </div>

            <div className="flex flex-col gap-3 border-t border-white/15 pt-5 mt-3">
              {user ? (
                <>
                  <Link to={portalPath(user.role)} onClick={() => setMobile(false)} className="btn-pill bg-ivory text-ink px-5 py-3 justify-center">Dashboard</Link>
                  <button type="button" className="text-left text-ivory/70 py-2" onClick={() => { logout(); setMobile(false); nav("/"); }}>Log out</button>
                </>
              ) : (
                <>
                  <Link to="/login" onClick={() => setMobile(false)} className="btn-pill border border-white/25 text-ivory px-5 py-3 justify-center">Log In</Link>
                  <Link to="/register" onClick={() => setMobile(false)} className="btn-pill bg-coral text-white px-5 py-3 justify-center">Get Started</Link>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
